import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { 
  ShieldCheckIcon, 
  BoltIcon, 
  CpuChipIcon, 
  DocumentCheckIcon, 
  ExclamationTriangleIcon,
  ArrowRightIcon,
  SparklesIcon,
  LockClosedIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const { login, demoLogin, user } = useAuth();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDemoLogin = async (role) => {
    setLoading(true);
    setError('');

    const result = await demoLogin(role);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  const fillCredentials = (userEmail) => {
    setEmail(userEmail);
    setPassword('demo123');
    setShowEmailForm(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col lg:flex-row relative overflow-hidden">
      {/* Dynamic Background Mesh Gradients */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-emerald-600/15 rounded-full blur-3xl"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:24px_24px] opacity-25"></div>
      </div>

      {/* LEFT COLUMN: Visual Showcase, Architecture & Statutory Frameworks */}
      <div className="relative z-10 lg:w-7/12 p-8 sm:p-12 lg:p-16 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-slate-800/80">
        <div>
          {/* Header Branding */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/25 border border-blue-400/30">
              <ShieldCheckIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl sm:text-2xl font-black tracking-tight text-white font-sans">
                  OpsSight AI
                </span>
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  2026 Ready
                </span>
              </div>
              <p className="text-xs text-slate-400 font-medium">Enterprise AI Governance & Dual-System Arbiter</p>
            </div>
          </div>

          {/* Operational Pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/90 border border-slate-700/80 text-xs text-slate-300 mb-6 backdrop-blur-sm">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="font-semibold text-emerald-400">Cloud Run Active</span>
            <span className="text-slate-600">&bull;</span>
            <span>us-central1</span>
            <span className="text-slate-600">&bull;</span>
            <span className="text-slate-400 font-mono text-[11px]">Reflex Latency &lt; 1ms</span>
          </div>

          {/* Hero Headline */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight mb-4">
            Autonomous AI Governance <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-emerald-400 bg-clip-text text-transparent">
              & Regulatory Reflex Arbiter
            </span>
          </h1>

          <p className="text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed mb-8">
            Continuous statutory oversight for the <strong className="text-white">EU AI Act (2024/1689)</strong>, <strong className="text-white">Colorado SB 24-205</strong>, and <strong className="text-white">NIST SP 1270-1</strong>. Enforce sub-millisecond edge guardrails, eliminate Article 5 prohibited liabilities, and maintain an immutable cryptographic audit ledger.
          </p>

          {/* Key Capabilities Showcase Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800/80 backdrop-blur-md">
              <div className="flex items-center gap-2.5 text-blue-400 font-bold text-sm mb-1.5">
                <ExclamationTriangleIcon className="w-5 h-5 text-rose-400" />
                EU Article 5 Defense Gate
              </div>
              <p className="text-xs text-slate-400 leading-normal">
                Blocks banned practices (biometric scraping, emotion inference) with real-time CI/CD pre-flight gatekeeper. Prevents up to €35M in statutory fines.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800/80 backdrop-blur-md">
              <div className="flex items-center gap-2.5 text-emerald-400 font-bold text-sm mb-1.5">
                <BoltIcon className="w-5 h-5 text-emerald-400" />
                Jev System 1 Reflex Engine
              </div>
              <p className="text-xs text-slate-400 leading-normal">
                Sub-millisecond (0.8ms) latency edge defense against OWASP LLM01 injection, zero-knowledge PII sanitization, and cryptographic HMAC sealing.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800/80 backdrop-blur-md">
              <div className="flex items-center gap-2.5 text-purple-400 font-bold text-sm mb-1.5">
                <CpuChipIcon className="w-5 h-5 text-purple-400" />
                GPAI Systemic Risk & FLOPs
              </div>
              <p className="text-xs text-slate-400 leading-normal">
                Automated detection for frontier models reaching the 10²⁵ FLOPs threshold with specialized Article 51-55 compliance workflows.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800/80 backdrop-blur-md">
              <div className="flex items-center gap-2.5 text-amber-400 font-bold text-sm mb-1.5">
                <DocumentCheckIcon className="w-5 h-5 text-amber-400" />
                FRIA & Board Audit Dossier
              </div>
              <p className="text-xs text-slate-400 leading-normal">
                Article 27 Fundamental Rights Impact Assessments, Colorado bias audits, and instant one-click board-ready PDF compliance dossiers.
              </p>
            </div>
          </div>

          {/* Statutory Framework Pills */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <span className="text-xs text-slate-400 font-medium mr-1">Compliance Frameworks:</span>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 border border-slate-700">
              EU AI Act 2024/1689
            </span>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 border border-slate-700">
              Colorado SB 24-205
            </span>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 border border-slate-700">
              NIST SP 1270-1 (AI RMF)
            </span>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 border border-slate-700">
              ISO/IEC 42001
            </span>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-8 pt-6 border-t border-slate-800/60 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-3">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-slate-400">
              <CheckBadgeIcon className="w-4 h-4 text-emerald-400" />
              SOC 2 Type II
            </span>
            <span className="flex items-center gap-1 text-slate-400">
              <LockClosedIcon className="w-4 h-4 text-blue-400" />
              HMAC-SHA256 Cryptographic Receipts
            </span>
          </div>
          <span className="font-mono text-[11px] text-slate-500">v2.4.0 • Google Cloud Run</span>
        </div>
      </div>

      {/* RIGHT COLUMN: Interactive Persona Sandbox & Authentication Portal */}
      <div className="relative z-10 lg:w-5/12 p-8 sm:p-12 flex flex-col justify-center bg-slate-900/60 backdrop-blur-xl">
        <div className="max-w-md w-full mx-auto">
          {/* Section Header */}
          <div className="text-center sm:text-left mb-6">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-md border border-blue-500/20 inline-block mb-2">
              Instant Access Sandbox
            </span>
            <h2 className="text-2xl font-bold text-white tracking-tight">
              Select Governance Persona
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Choose an enterprise role to explore tailored statutory workflows, or launch the executive overview.
            </p>
          </div>

          {error && (
            <div className="mb-4 bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs px-4 py-3 rounded-lg flex items-center gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          {/* Quick Launch Primary Action */}
          <button
            onClick={() => handleDemoLogin('Executive')}
            disabled={loading}
            className="w-full mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:via-indigo-500 hover:to-purple-500 text-white font-bold shadow-lg shadow-indigo-600/30 border border-indigo-400/40 flex items-center justify-between transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white animate-soft-pulse" />
              </div>
              <div className="text-left">
                <div className="text-sm font-extrabold flex items-center gap-1.5">
                  Launch Live Executive Portal
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.2 rounded font-normal uppercase">
                    1-Click
                  </span>
                </div>
                <div className="text-[11px] text-blue-100 font-normal">
                  Full access: Board Dossier, Stat Cards & Gauge
                </div>
              </div>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* 3 Interactive Persona Cards */}
          <div className="space-y-3 mb-6">
            {/* Persona 1: Executive */}
            <div 
              onClick={() => handleDemoLogin('Executive')}
              className="p-3.5 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 hover:border-purple-500/50 cursor-pointer transition-all hover:-translate-y-0.5 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center justify-center font-bold text-sm">
                    CRO
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors">
                        Executive / Board Mode
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-medium">
                        Mike Executive
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Statutory liability monitoring (€35M fine exposure) & Board Audit Dossier export.
                    </p>
                  </div>
                </div>
                <ArrowRightIcon className="w-4 h-4 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all mt-1" />
              </div>
            </div>

            {/* Persona 2: DPO */}
            <div 
              onClick={() => handleDemoLogin('DPO')}
              className="p-3.5 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 hover:border-blue-500/50 cursor-pointer transition-all hover:-translate-y-0.5 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center justify-center font-bold text-sm">
                    DPO
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white group-hover:text-blue-300 transition-colors">
                        Data Protection Officer
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 font-medium">
                        Sarah DPO
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Article 27 FRIA sign-offs, Colorado SB 205 bias reviews, and seal authorization.
                    </p>
                  </div>
                </div>
                <ArrowRightIcon className="w-4 h-4 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all mt-1" />
              </div>
            </div>

            {/* Persona 3: Developer */}
            <div 
              onClick={() => handleDemoLogin('Developer')}
              className="p-3.5 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 hover:border-emerald-500/50 cursor-pointer transition-all hover:-translate-y-0.5 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center justify-center font-bold text-sm">
                    DEV
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors">
                        AI Engineer / MLOps
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-medium">
                        John Developer
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Onboard foundation models, FLOPs compute classifier, and Jev edge reflex integration.
                    </p>
                  </div>
                </div>
                <ArrowRightIcon className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all mt-1" />
              </div>
            </div>
          </div>

          {/* Toggle Email Credentials */}
          <div className="border-t border-slate-800/80 pt-4">
            <button
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition-colors flex items-center justify-center gap-1.5"
            >
              <LockClosedIcon className="w-3.5 h-3.5" />
              <span>{showEmailForm ? 'Hide standard login form' : 'Or sign in with custom enterprise credentials'}</span>
            </button>

            {showEmailForm && (
              <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Work Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@enterprise.com"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-sm transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Authenticating...' : 'Sign In'}
                  </button>
                  <button
                    type="button"
                    onClick={() => fillCredentials('executive@demo.com')}
                    className="py-2 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs border border-slate-700"
                  >
                    Prefill Demo
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;